// Note - Till this note is deleted, the data inside this page only has sample data.

// Note - This data structure is now aligned with Zoho Desk API format
// Key changes are documented at the bottom of this file

// API function to fetch tickets from Zoho Desk via backend
import apiClient from './apiClient'; // Import the central, authenticated apiClient instance

/**
 * Handles the special Zoho re-authentication error from the backend.
 * Instead of redirecting automatically, it now returns the auth URL
 * so the UI can handle it gracefully.
 * 
 * @param {Object} error - The error object from axios
 * @returns {Object|null} - Auth info object or null if not a Zoho auth error
 */
function handleZohoAuthError(error) {
    if (error.response?.data?.requiresZohoAuth) {
      const { authUrl } = error.response.data;
      if (authUrl) {
        // Save the current page's path to return to after the Zoho auth flow
        sessionStorage.setItem('zoho_auth_return_url', window.location.pathname);
        
        // Return auth info instead of redirecting
        return {
          requiresZohoAuth: true,
          authUrl: authUrl,
          message: "Zoho authentication required"
        };
      }
    }
    return null;
  }

/**
 * Detects 5xx server / Zoho Desk gateway errors so the app can degrade gracefully.
 * @param {Object} error - The error object from axios
 * @returns {Object|null} - { isZohoGatewayError: true, message: string } or null
 */
function getZohoGatewayError(error) {
  const status = error.response?.status;
  if (status >= 500 && status < 600) {
    return {
      isZohoGatewayError: true,
      message: "Zoho Desk gateway is temporarily unavailable. You can still use the rest of the app. Tickets will load when the service is back."
    };
  }
  return null;
}

const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL || 'https://localhost:5000'}/api/zoho/desk`;

/**
 * Fetches all tickets from Zoho Desk using the protected backend endpoint.
 * This function no longer needs a getToken parameter because apiClient handles it.
 */
// All functions now use apiClient to automatically send the Auth0 token
export const fetchTickets = async (params = {}) => {
    try {
      const response = await apiClient.get('/api/zoho/desk/tickets', { params });
      return Array.isArray(response.data) ? response.data : response.data.data || [];
    } catch (error) {
      console.error('Error fetching Zoho tickets:', error);
      
      const zohoAuthInfo = handleZohoAuthError(error);
      if (zohoAuthInfo) {
        throw Object.assign(new Error(zohoAuthInfo.message), zohoAuthInfo);
      }
      
      const gatewayErr = getZohoGatewayError(error);
      if (gatewayErr) {
        throw Object.assign(new Error(gatewayErr.message), gatewayErr);
      }
      
      throw error;
    }
  };
  
  export const createTicket = async (ticketData) => {
    try {
      console.log('=== API: createTicket - Request Details ===');
      console.log('🔍 Account ID in request:', ticketData.accountId);
      console.log('🔍 Full request payload:', JSON.stringify(ticketData, null, 2));
      console.log('🔍 Request URL: POST /api/zoho/desk/tickets');
      console.log('===========================================');
      
      const response = await apiClient.post('/api/zoho/desk/tickets', ticketData);
      
      console.log('=== API: createTicket - Response Details ===');
      console.log('📥 Response accountId:', response.data?.accountId);
      console.log('📥 Response accountName:', response.data?.accountName);
      console.log('📥 Full response:', JSON.stringify(response.data, null, 2));
      console.log('===========================================');
      
      return response.data;
    } catch (error) {
      console.error('=== API: createTicket - Error ===');
      console.error('Error details:', error);
      console.error('Error response:', error.response?.data);
      console.error('Request that failed:', ticketData);
      console.error('==================================');
      
      // Check for Zoho auth error
      const zohoAuthInfo = handleZohoAuthError(error);
      if (zohoAuthInfo) {
        // Throw a custom error with the auth info
        throw Object.assign(new Error(zohoAuthInfo.message), zohoAuthInfo);
      }
      
      // Throw the original error if not a Zoho auth error
      throw error;
    }
  };

export const fetchTicket = async (ticketId) => {
    try {
        const response = await apiClient.get(`/api/zoho/desk/tickets/${ticketId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching ticket:', error);
        
        // Check for Zoho auth error
        const zohoAuthInfo = handleZohoAuthError(error);
        if (zohoAuthInfo) {
          // Throw a custom error with the auth info
          throw Object.assign(new Error(zohoAuthInfo.message), zohoAuthInfo);
        }
        
        throw error;
    }
};

export const updateTicket = async (ticketId, updateData) => {
    try {
        const response = await apiClient.patch(`/api/zoho/desk/tickets/${ticketId}`, updateData);
        return response.data;
    } catch (error) {
        console.error('Error updating ticket:', error);
        
        // Check for Zoho auth error
        const zohoAuthInfo = handleZohoAuthError(error);
        if (zohoAuthInfo) {
          // Throw a custom error with the auth info
          throw Object.assign(new Error(zohoAuthInfo.message), zohoAuthInfo);
        }
        
        throw error;
    }
};

/**
 * Fetches account details from Zoho Desk using the account ID
 * @param {string} accountId - The account ID to fetch details for
 * @returns {Promise<Object>} Account details including accountName
 */
export const fetchAccountDetails = async (accountId) => {
    try {
        const response = await apiClient.get(`/api/zoho/desk/accounts/${accountId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching account details:', error);
        
        // Check for Zoho auth error
        const zohoAuthInfo = handleZohoAuthError(error);
        if (zohoAuthInfo) {
          // Throw a custom error with the auth info
          throw Object.assign(new Error(zohoAuthInfo.message), zohoAuthInfo);
        }
        
        throw error;
    }
};

/**
 * Fetches all accounts from Zoho Desk for client filtering
 * @returns {Promise<Array>} Array of accounts with accountId and accountName
 */
export const fetchAllAccounts = async () => {
    try {
        const response = await apiClient.get('/api/zoho/desk/accounts');
        return Array.isArray(response.data) ? response.data : response.data.data || [];
    } catch (error) {
        console.error('Error fetching all accounts:', error);
        
        // Check for Zoho auth error
        const zohoAuthInfo = handleZohoAuthError(error);
        if (zohoAuthInfo) {
          // Throw a custom error with the auth info
          throw Object.assign(new Error(zohoAuthInfo.message), zohoAuthInfo);
        }
        
        throw error;
    }
};

/**
 * Creates a new account in Zoho Desk
 * @param {Object} accountData - Account data including accountName
 * @returns {Promise<Object>} Created account details
 */
export const createAccount = async (accountData) => {
    try {
        const response = await apiClient.post('/api/zoho/desk/accounts', accountData);
        return response.data;
    } catch (error) {
        console.error('Error creating account:', error);
        
        // Check for Zoho auth error
        const zohoAuthInfo = handleZohoAuthError(error);
        if (zohoAuthInfo) {
          // Throw a custom error with the auth info
          throw Object.assign(new Error(zohoAuthInfo.message), zohoAuthInfo);
        }
        
        throw error;
    }
};

/**
 * Finds a Zoho account by accountName
 * @param {string} accountName - The account name to search for
 * @returns {Promise<Object|null>} Account details if found, null otherwise
 */
export const findAccountByName = async (accountName) => {
    try {
        const accounts = await fetchAllAccounts();
        return accounts.find(account => 
            account.accountName?.toLowerCase() === accountName?.toLowerCase()
        ) || null;
    } catch (error) {
        console.error('Error finding account by name:', error);
        throw error;
    }
};

/**
 * Creates a new contact in Zoho Desk
 * @param {Object} contactData - Contact data including email, firstName, lastName, accountId
 * @returns {Promise<Object>} Created contact details
 */
export const createContact = async (contactData) => {
    try {
        const response = await apiClient.post('/api/zoho/desk/contacts', contactData);
        return response.data;
    } catch (error) {
        console.error('Error creating contact:', error);
        
        // Check for Zoho auth error
        const zohoAuthInfo = handleZohoAuthError(error);
        if (zohoAuthInfo) {
          // Throw a custom error with the auth info
          throw Object.assign(new Error(zohoAuthInfo.message), zohoAuthInfo);
        }
        
        throw error;
    }
};

/**
 * Fetches all contacts from a specific Zoho Desk account
 * @param {string} accountId - The account ID to fetch contacts for
 * @returns {Promise<Array>} Array of contacts
 */
export const fetchContactsByAccount = async (accountId) => {
    try {
        const response = await apiClient.get(`/api/zoho/desk/contacts`, { 
            params: { accountId } 
        });
        return Array.isArray(response.data) ? response.data : response.data.data || [];
    } catch (error) {
        console.error('Error fetching contacts by account:', error);
        
        // Check for Zoho auth error
        const zohoAuthInfo = handleZohoAuthError(error);
        if (zohoAuthInfo) {
          // Throw a custom error with the auth info
          throw Object.assign(new Error(zohoAuthInfo.message), zohoAuthInfo);
        }
        
        throw error;
    }
};

/*
Key Changes Made to Align with Zoho Desk API:

1. Field Name Changes:
   - 'title' → 'subject'
   - 'active.status' → 'status'
   - 'additional_info.priority' → 'priority'
   - 'additional_info.category' → 'classification'
   - 'created.date' → 'createdTime'
   - 'active.assigned_to' → 'assignee'

2. New Required Fields:
   - ticketNumber
   - channel
   - language
   - isTrashed
   - modifiedTime
   - closedTime

3. Restructured Fields:
   - Contact information moved to dedicated 'contact' object
   - Department information added as separate object
   - Assignee information structured as object
   - Conversations renamed to 'threads' with additional metadata
   - Status history tracked separately

4. Removed Fields:
   - 'active.assigned_on'
   - 'active.modified'
   - Simplified resolution tracking

5. Added Zoho-specific Fields:
   - Custom fields section for additional data
   - Thread IDs and public/private flags
   - Proper status history tracking

Note: When creating tickets via Zoho Desk API, only the following fields are required:
- subject
- description
- contactId
- departmentId
- classification
- priority
- channel
- language

All other fields are optional and will be set to default values if not provided.
*/